import React from 'react';
import { Link } from 'react-router-dom';
import { Event } from '../types';

interface EventCardProps {
  event: Event;
}

const EventCard: React.FC<EventCardProps> = ({ event }) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const availableStands = event.stands.reduce((sum, stand) => sum + stand.available, 0);
  const totalStands = event.stands.reduce((sum, stand) => sum + stand.total, 0);
  const occupancyRate = totalStands > 0 ? ((totalStands - availableStands) / totalStands) * 100 : 0;

  return (
    <Link to={`/evento/${event.id}`} className="block group">
      <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-primary-300">
        {/* Banner Image */}
        <div className="relative h-48 overflow-hidden">
          <img
            src={event.bannerImg}
            alt={event.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          {availableStands === 0 && (
            <div className="absolute top-3 right-3 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
              Agotado
            </div>
          )}
          {availableStands > 0 && availableStands <= 5 && (
            <div className="absolute top-3 right-3 bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
              ¡Últimos cupos!
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-5">
          <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-primary-600 transition-colors">
            {event.name}
          </h3>
          
          <div className="flex items-center text-gray-600 mb-2">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="text-sm">{event.location}</span>
          </div>

          <div className="flex items-center text-gray-600 mb-3">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-sm">
              {formatDate(event.startDate)} - {formatDate(event.endDate)}
            </span>
          </div>

          <p className="text-gray-600 text-sm mb-4 line-clamp-2">
            {event.description}
          </p>

          {/* Stats */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
            <div className="flex items-center space-x-2">
              <div className="flex -space-x-2">
                {event.participants.slice(0, 3).map((_, idx) => (
                  <div
                    key={idx}
                    className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 border-2 border-white flex items-center justify-center text-white text-xs font-semibold"
                  >
                    {idx + 1}
                  </div>
                ))}
              </div>
              <span className="text-sm text-gray-600">
                {event.participants.length} emprendedores
              </span>
            </div>
            <div className="text-right">
              <div className="text-sm font-semibold text-gray-800">
                {availableStands} / {totalStands}
              </div>
              <div className="text-xs text-gray-500">stands disponibles</div>
            </div>
          </div>

          {/* Occupancy Bar */}
          <div className="mt-3">
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  occupancyRate >= 90 ? 'bg-red-500' : occupancyRate >= 70 ? 'bg-orange-500' : 'bg-green-500'
                }`}
                style={{ width: `${occupancyRate}%` }}
              />
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default EventCard;
